import { Injectable } from '@angular/core';
import { ParentApiService } from './parent-api-service';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CreateDocRequestDto } from '../entities/doc-entities/create-doc-request-dto';
import { CreateDocFailedResponseDto } from '../entities/doc-entities/create-doc-failed-response-dto';
import { CreateDocTdRequestDto } from '../entities/doc-entities/create-doc-td-request';
import { PaginationQueryDto } from '../entities/pagination-query-dto';
import { PaginationResponseDto } from '../entities/pagination-response-dto';
import { PpwrPackagingInfoDto } from '../entities/ppwr-entities/ppwr-packaging-info-dto';
import { SetPpwrApprovedRequestDto } from '../entities/ppwr-entities/set-ppwr-approved-request-dto';
import { DocProfileDto } from '../entities/doc-entities/doc-profile-dto';
import { ProfileSaveResultDto } from '../entities/doc-entities/profile-save-result-dto';
import { DatasetProfileDto } from '../entities/doc-entities/dataset-profile-dto';

@Injectable({
  providedIn: 'root',
})
export class PpwrApiService extends ParentApiService {
  private readonly baseUrl = environment.webApiAddress + '/api';

  constructor(private readonly http: HttpClient) {
    super();
  }

  getDocProblemsForUnits(packages: CreateDocTdRequestDto, stayOnPageIfError?: boolean): Observable<CreateDocFailedResponseDto[]> {
    console.log('getDocProblemsForUnits request:', packages);
    return this.http.post<CreateDocFailedResponseDto[]>(`${this.baseUrl}/packaging-units/doc/problem-information`, packages, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  getDocProblemsForSystems(packages: CreateDocTdRequestDto, stayOnPageIfError?: boolean): Observable<CreateDocFailedResponseDto[]> {
    return this.http.post<CreateDocFailedResponseDto[]>(`${this.baseUrl}/packaging-systems/doc/problem-information`, packages, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
    });
  }

  generateTechnicalDocumentationForUnits(packages: CreateDocTdRequestDto, stayOnPageIfError?: boolean): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/packaging-units/generate-technical-documentation`, packages, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
      responseType: 'blob',
    });
  }

  generateTechnicalDocumentationForSystems(packages: CreateDocTdRequestDto, stayOnPageIfError?: boolean): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/packaging-systems/generate-technical-documentation`, packages, {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
      responseType: 'blob',
    });
  }

  generateDocForUnits(request: CreateDocRequestDto, stayOnPageIfError?: boolean): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/packaging-units/generate-signed-doc`, this.buildGenerateSignedDocBody(request), {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
      responseType: 'blob',
    });
  }

  generateDocForSystems(request: CreateDocRequestDto, stayOnPageIfError?: boolean): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/packaging-systems/generate-signed-doc`, this.buildGenerateSignedDocBody(request), {
      headers: stayOnPageIfError ? this.stayOnPageIfErrorHeader : this.emptyHeader,
      responseType: 'blob',
    });
  }

  private buildGenerateSignedDocBody(request: CreateDocRequestDto): FormData {
    const { signatureImage, ...rest } = request;
    const formData = new FormData();
    formData.append('input', JSON.stringify(rest));
    if (signatureImage) {
      formData.append('signatureImage', signatureImage, 'signature.png');
    }
    return formData;
  }

  getPpwrInfoForSystemsByPage(paginationQuery: PaginationQueryDto): Observable<PaginationResponseDto<PpwrPackagingInfoDto>> {
    return this.http.post<PaginationResponseDto<PpwrPackagingInfoDto>>(`${this.baseUrl}/packaging-systems/ppwr-info/by-page`, paginationQuery);
  }

  getPpwrInfoForUnitsByPage(paginationQuery: PaginationQueryDto): Observable<PaginationResponseDto<PpwrPackagingInfoDto>> {
    return this.http.post<PaginationResponseDto<PpwrPackagingInfoDto>>(`${this.baseUrl}/packaging-units/ppwr-info/by-page`, paginationQuery);
  }

  setPpwrApprovedForSystem(request: SetPpwrApprovedRequestDto): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/packaging-systems/set-ppwr-approved`, request);
  }

  setPpwrApprovedForUnit(request: SetPpwrApprovedRequestDto): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/packaging-units/set-ppwr-approved`, request);
  }

  getAllDocProfiles(): Observable<DocProfileDto[]> {
    return this.http.get<DocProfileDto[]>(`${this.baseUrl}/doc-profiles`);
  }

  createOrUpdateDocProfile(profile: DocProfileDto): Observable<ProfileSaveResultDto> {
    return this.http.put<ProfileSaveResultDto>(`${this.baseUrl}/doc-profiles`, profile);
  }

  deleteDocProfile(id: string, version?: number): Observable<void> {
    let params = new HttpParams().set('id', id);
    if (version != null) {
      params = params.set('version', version);
    }
    return this.http.delete<void>(`${this.baseUrl}/doc-profiles`, { params, headers: this.stayOnPageIfErrorHeader });
  }

  getAllPackagingSystemProfiles(): Observable<DatasetProfileDto[]> {
    return this.http.get<DatasetProfileDto[]>(`${this.baseUrl}/packaging-system-profiles`);
  }

  createOrUpdatePackagingSystemProfile(profile: DatasetProfileDto): Observable<ProfileSaveResultDto> {
    return this.http.put<ProfileSaveResultDto>(`${this.baseUrl}/packaging-system-profiles`, profile);
  }

  deletePackagingSystemProfile(id: string, version?: number): Observable<void> {
    let params = new HttpParams().set('id', id);
    if (version != null) {
      params = params.set('version', version);
    }
    return this.http.delete<void>(`${this.baseUrl}/packaging-system-profiles`, { params, headers: this.stayOnPageIfErrorHeader });
  }

  getAllPackagingUnitProfiles(): Observable<DatasetProfileDto[]> {
    return this.http.get<DatasetProfileDto[]>(`${this.baseUrl}/packaging-unit-profiles`);
  }

  createOrUpdatePackagingUnitProfile(profile: DatasetProfileDto): Observable<ProfileSaveResultDto> {
    return this.http.put<ProfileSaveResultDto>(`${this.baseUrl}/packaging-unit-profiles`, profile);
  }

  deletePackagingUnitProfile(id: string, version?: number): Observable<void> {
    let params = new HttpParams().set('id', id);
    if (version != null) {
      params = params.set('version', version);
    }
    return this.http.delete<void>(`${this.baseUrl}/packaging-unit-profiles`, { params, headers: this.stayOnPageIfErrorHeader });
  }

  getAllMultiMaterialProfiles(): Observable<DatasetProfileDto[]> {
    return this.http.get<DatasetProfileDto[]>(`${this.baseUrl}/multi-material-profiles`);
  }

  createOrUpdateMultiMaterialProfile(profile: DatasetProfileDto): Observable<ProfileSaveResultDto> {
    return this.http.put<ProfileSaveResultDto>(`${this.baseUrl}/multi-material-profiles`, profile);
  }

  deleteMultiMaterialProfile(id: string, version?: number): Observable<void> {
    let params = new HttpParams().set('id', id);
    if (version != null) {
      params = params.set('version', version);
    }
    return this.http.delete<void>(`${this.baseUrl}/multi-material-profiles`, { params, headers: this.stayOnPageIfErrorHeader });
  }

  getAllPackagingComponentProfiles(): Observable<DatasetProfileDto[]> {
    return this.http.get<DatasetProfileDto[]>(`${this.baseUrl}/packaging-component-profiles`);
  }

  createOrUpdatePackagingComponentProfile(profile: DatasetProfileDto): Observable<ProfileSaveResultDto> {
    return this.http.put<ProfileSaveResultDto>(`${this.baseUrl}/packaging-component-profiles`, profile);
  }

  deletePackagingComponentProfile(id: string, version?: number): Observable<void> {
    let params = new HttpParams().set('id', id);
    if (version != null) {
      params = params.set('version', version);
    }
    return this.http.delete<void>(`${this.baseUrl}/packaging-component-profiles`, { params, headers: this.stayOnPageIfErrorHeader });
  }
}
